import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import Table from "@components/Table";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { ActionsType } from "src/interfaces/Common";
import { MethodType } from "src/services/type";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useParams } from "react-router-dom";
import ModalCst from "@components/ModalCst";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import InfoBar from "./Components/InfoBar";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CalculateIcon from "@mui/icons-material/Calculate";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const CostProject: React.FC = () => {
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [openModalManage, setOpenModalManage] = useState<boolean>(false);
  const [openModalAccounting, setOpenModalAccounting] =
    useState<boolean>(false);
  const [idCostDeleted, setIdCostDeleted] = useState<string>();
  const [idCostEdit, setIdCostEdit] = useState<string>();
  const [idCostManage, setIdCostManage] = useState<string>();
  const [idCostAccounting, setIdCostAccounting] = useState<string>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const { idProject } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["projects-cost-page"]);
  const { t: tOptions } = useTranslation(["input"]);

  const { user } = useAuth();
  const { filters } = useFilters();

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getProjectsCost}/?budgetProjectId=${idProject}`,
      PagesID["projects.cost"],
      filters[`${user?.id_user}_${PagesID["projects.cost"]}`],
    );

  const { dataTable: dataStats, mutate: mutateStats } = useFetchTable(
    `${ServicesURL.getProjectsCost}/stats/?budgetProjectId=${idProject}`,
    PagesID["projects.cost"],
    filters[`${user?.id_user}_${PagesID["projects.cost"]}`],
  );

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const onSubmitModal = async () => {
    setIdCostEdit(undefined);
    setIdCostManage(undefined);
    setIdCostAccounting(undefined);
    mutate && mutate();
    mutateStats && mutateStats();
  };

  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.budget_project_categorization) {
        el.budget_project_categorization.label =
          el.budget_project_categorization?.macro_category?.title || "-";
        el.available_budget =
          el.budget_project_categorization?.available_budget || "-";
      }

      if (el.contract) {
        el.contract.label = el.contract?.sap_code;
        if (el.contract?.supplier) {
          el.supplier = el.contract?.supplier || {};
          el.supplier.label = el.contract.supplier.business_name;
        }
      } else {
        el.contract = { label: "-" };
        el.supplier = { label: "-" };
      }

      el.management_type = el.management_type || el.request_type;

      return el;
    });
  }, [dataTable?.output]);

  const { trigger: triggerDeleteCost } = useSWRMutation(
    `${ServicesURL.postProjectsCost}/${idCostDeleted}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idCostDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteCost();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idCostDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCostDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idCostDeleted, mutate, triggerDeleteCost]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setIdCostDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        case "button": {
          switch (action.key) {
            case "project-cost-edit":
              return {
                ...action,
                getIcon: () => <EditIcon />,
                callBack: (data: any) => {
                  setIdCostEdit(data);
                  setOpenModalEdit(true);
                },
              };
            case "project-cost-manage":
              return {
                ...action,
                getIcon: () => <CurrencyExchangeIcon />,
                callBack: (data: any) => {
                  setIdCostManage(data);
                  setOpenModalManage(true);
                },
              };
            case "project-cost-accounting":
              return {
                ...action,
                getIcon: () => <CalculateIcon />,
                callBack: (data: any) => {
                  setIdCostAccounting(data);
                  setOpenModalAccounting(true);
                },
              };
            case "project-cost-receipts":
              return {
                ...action,
                callBack: (row: any) => {
                  navigate(`goods-receipts/${row.id}`);
                },
                getIcon: () => {
                  return <LocalShippingIcon />;
                },
              };
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    if (row.original.status !== "NEW") {
      configRowNew = configRowNew.filter(
        (action: ActionsType) =>
          !["project-cost-delete", "project-cost-edit"].includes(
            action.key ?? "",
          ),
      );
    }

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }

      if (
        el.key === "project-cost-receipts" &&
        !(
          (row.original?.status === "PO_ISSUED" &&
            row.original?.management_type === "RDA") ||
          (row.original?.status === "APPROVED" &&
            row.original?.management_type === "BC_BE")
        )
      ) {
        return { ...el, disabled: true };
      }

      if (el.key === "project-cost-accounting" && row.original?.internal) {
        return { ...el, type: "" };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "button":
          return {
            ...action,
            getIcon: () => <AddIcon />,
            callBack: () => setOpenModal(true),
          };
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value !== undefined && value !== null && value
      ? tOptions("options.switch_key.true")
      : tOptions("options.switch_key.false");
  };

  return (
    <AnimationFadeIn>
      <Grid container>
        <InfoBar data={dataStats} />
      </Grid>
      <Table
        id={PagesID.projects}
        data={dataFormat || []}
        columns={filteredColumns || []}
        configurationTopToolbar={actionsToolbar}
        renderRowActions={customRenderRowActions}
        numberOfActions={configurationActionForRow?.length}
        totalPages={dataTable?.metadata?.total_elements}
        modalTitle={t("hero-title")?.replace("-", "") || ""}
        renderCell={renderCell}
      />
      <ModalCst
        open={openModal}
        setOpen={setOpenModal}
        typeOfModal={"AddCostProject"}
        onSubmit={onSubmitModal}
        fullWidth={true}
      />
      <ModalCst
        open={openModalEdit}
        setOpen={setOpenModalEdit}
        typeOfModal={"EditCostProject"}
        onSubmit={onSubmitModal}
        data={idCostEdit}
        fullWidth={true}
      />
      <ModalCst
        open={openModalManage}
        setOpen={setOpenModalManage}
        typeOfModal={"ManageCostProject"}
        onSubmit={onSubmitModal}
        data={idCostManage}
        fullWidth={true}
      />
      <ModalCst
        open={openModalAccounting}
        setOpen={setOpenModalAccounting}
        typeOfModal={"AccountingCostProject"}
        onSubmit={onSubmitModal}
        data={idCostAccounting}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default CostProject;
